import React, { useEffect, useRef, useState } from "react";
import { Filter, SlidersHorizontal, X } from "lucide-react";
import { cx } from "./format";

/**
 * Header button that opens/closes a <FilterPanel>. The sliders icon spins into
 * an X while open; `count` shows a badge with the number of active filters.
 */
export const FilterToggle = ({ open, onToggle, count = 0, label = "Filters", controls, className }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-expanded={open}
    aria-controls={controls}
    className={cx(
      "cm-filter-toggle group relative inline-flex h-10 items-center gap-2 rounded-cm border px-4 text-sm font-semibold",
      "transition-[background-color,border-color,color,box-shadow,transform] duration-300 active:scale-[0.97]",
      "focus-visible:outline-none focus-visible:shadow-cm-focus",
      open
        ? "border-cm-gold bg-cm-gold-soft text-cm-gold-text"
        : "border-cm-border bg-cm-surface-2 text-cm-text hover:border-cm-border-strong",
      className,
    )}
    data-open={open}
  >
    <span className="relative inline-flex h-[18px] w-[18px]" aria-hidden="true">
      <SlidersHorizontal size={18} className="cm-filter-toggle__icon cm-filter-toggle__icon--sliders" />
      <X size={18} className="cm-filter-toggle__icon cm-filter-toggle__icon--close" />
    </span>
    {label}
    {count > 0 && (
      <span
        key={count}
        className="cm-filter-badge inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-cm-gold px-1.5 text-[11px] font-bold text-cm-gold-ink tabular-nums"
      >
        {count}
      </span>
    )}
  </button>
);

/**
 * Collapsible filter area with a smooth height/opacity/slide transition and a
 * staggered entrance for each child. Wrap each field group in <FilterItem>.
 * Overflow is only clipped while animating, so dropdown menus inside are not cut off.
 */
export const FilterPanel = ({ open, id, children, className }) => {
  const [settled, setSettled] = useState(open);
  const first = useRef(true);

  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    setSettled(false);
    // Unclip once the height transition is done (matches the CSS duration)
    const t = setTimeout(() => setSettled(true), 480);
    return () => clearTimeout(t);
  }, [open]);

  return (
    <div
      id={id}
      className="cm-filter-panel"
      data-open={open}
      data-settled={open && settled}
      aria-hidden={!open}
      inert={open ? undefined : ""}
    >
      <div className="cm-filter-panel__inner">
        <div className={cx("cm-filter-panel__body", className)}>{children}</div>
      </div>
    </div>
  );
};

/** One staggered block inside a FilterPanel; `index` sets its entrance delay. */
export const FilterItem = ({ index = 0, className, children }) => (
  <div className={cx("cm-filter-item min-w-0", className)} style={{ "--cm-i": index }}>
    {children}
  </div>
);

/** Compact pill that summarises an active filter while the panel is closed. */
export const FilterChip = ({ icon: Icon, children, onClick, index = 0 }) => (
  <button
    type="button"
    onClick={onClick}
    className="cm-filter-chip inline-flex max-w-full items-center gap-1.5 rounded-full border border-cm-border bg-cm-surface-2 px-3 py-1.5 text-[13px] font-medium text-cm-text transition-colors hover:border-cm-gold hover:text-cm-gold-text"
    style={{ "--cm-i": index }}
  >
    {Icon && <Icon size={14} className="shrink-0 text-cm-muted" aria-hidden="true" />}
    <span className="truncate">{children}</span>
  </button>
);

/**
 * Card with a collapsible, animated filter area. Closed, it shows the active
 * filters as chips (click one to open); open, the fields slide down and each
 * one rises in turn. Pass the toggle state in; put <FilterToggle> in the page
 * header, or set `inlineToggle` to render it inside the card.
 *
 * chips: [{ icon?, label }] — one per active filter.
 */
export const FilterCard = ({
  open, onOpenChange, chips = [], emptyLabel = "No filters applied", openLabel = "Refine results",
  aside, inlineToggle = false, id, className, bodyClassName, children,
}) => (
  <section className={cx("bg-cm-surface border border-cm-border rounded-cm-lg min-w-0 p-4 sm:p-5", className)}>
    <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-3">
      {open ? (
        <p className="flex items-center gap-1.5 text-[13px] font-semibold text-cm-text m-0 animate-cm-fade-in">
          <Filter size={14} className="text-cm-gold-text" aria-hidden="true" />
          {openLabel}
        </p>
      ) : (
        <div className="flex flex-wrap items-center gap-2 min-w-0">
          {chips.length ? (
            chips.map((chip, i) => (
              <FilterChip key={`${i}-${chip.label}`} icon={chip.icon} index={i} onClick={() => onOpenChange(true)}>
                {chip.label}
              </FilterChip>
            ))
          ) : (
            <FilterChip icon={Filter} onClick={() => onOpenChange(true)}>{emptyLabel}</FilterChip>
          )}
        </div>
      )}
      {(aside || inlineToggle) && (
        <div className="flex flex-wrap items-center gap-3 ml-auto">
          {aside}
          {inlineToggle && (
            <FilterToggle open={open} onToggle={() => onOpenChange(!open)} count={chips.length} controls={id} />
          )}
        </div>
      )}
    </div>
    <FilterPanel open={open} id={id}>
      <div className={cx("pt-4 cm-filter-stagger", bodyClassName)}>{children}</div>
    </FilterPanel>
  </section>
);
